import axios from 'axios'
import store from '@/store'

/**
 * PoolinqAiService
 *
 * API Service Layer for the poolinq AI backend (port 3131).
 *
 * All endpoints require a Strapi JWT bearer token for authentication, which is
 * read from the store and attached to every request.
 *
 * Endpoints (DEV):
 *   GET  /list_outbound_sheet_rows?venue_id=31&date=08-06-2026
 *   GET  /list_inbound_sheet_rows?venue_id=31&invoice_number=RG2613067&invoice_date=08-06-2026
 *   POST /update_sheet_rows
 */

// DEV server poolinq AI = 'http://localhost:3131'
var serverAi = window.location.protocol + '//' + window.location.hostname + ':3131'

// TEST or PROD server 'http://domain/ai-tools'
// The AI backend is proxied by nginx under /ai-tools on dev/integration/prod
// (see poolinq_ai .google_app_scripts and google_api_auth.ts).
if (window.location.href.indexOf(':8080') < 0 && window.location.href.indexOf(':8000') < 0) {
  serverAi = window.location.protocol + '//' + window.location.hostname + '/ai-tools'
}

// Add jwt auth token to request
axios.interceptors.request.use(function (config) {
  const token = 'Bearer ' + store.getters.getJwt
  config.headers.Authorization = token
  return config
})

export default {

  getServerUrl() {
    return serverAi
  },

  /**
   * Get Outbound Sheet Rows
   *
   * @param {number} venueId
   * @param {string} date - format 'DD-MM-YYYY' e.g. '08-06-2026'
   */
  getOutboundSheetRows(venueId, date) {
    return axios.get(serverAi + '/list_outbound_sheet_rows', {
      params: {
        venue_id: venueId,
        date: date,
      },
    })
  },

  /**
   * Get All Outbound Sheet Rows of a Month
   * @param {number} venueId 
   * @param {string} month - format "January", "February", etc.
   * @param {string} year - 2026
   * @returns 
   */
  getAllOutboundSheetRowsOfMonth(venueId, month, year) {
    return axios.get(serverAi + '/list_all_outbound_sheet_rows', {
      params: {
        venue_id: venueId,
        month: month,
        year: year,
      },
    })
  },

  /**
   * Get Inbound Sheet Rows
   *
   * @param {number} venueId
   * @param {string} invoiceNumber - e.g. 'RG2613067'
   * @param {string} invoiceDate - format 'DD-MM-YYYY' e.g. '08-06-2026'
   */
  getInboundSheetRows(venueId, invoiceNumber, invoiceDate) {
    return axios.get(serverAi + '/list_inbound_sheet_rows', {
      params: {
        venue_id: venueId,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
      },
    })
  },

  /**
   * Get All Inbound Sheet Rows of a Month
   * @param {number} venueId 
   * @param {string} month - format "January", "February", etc.
   * @param {string} year - 2026
   * @returns 
   */
  getAllInboundSheetRowsOfMonth(venueId, month, year) {
    return axios.get(serverAi + '/list_all_inbound_sheet_rows', {
      params: {
        venue_id: venueId,
        month: month,
        year: year,
      },
    })
  },

  /**
   * Update Sheet Rows
   *
   * @param {string} sheetId - the google sheet id
   * @param {number} venueId
   * @param {Array} rows - array of SheetRow objects
   */
  updateSheetRows(sheetId, venueId, rows) {
    return axios.post(serverAi + '/update_sheet_rows', {
      sheet_id: sheetId,
      venue_id: venueId,
      rows: rows,
    })
  },

  /**
   * Start Updater With Sheet
   *
   * Starts the updater for a given google sheet, optionally with pre-matched rows.
   *
   * @param {number} venueId
   * @param {string} sheetId - the google sheet id
   * @param {string} sheetType - e.g. 'outbound' | 'inbound'
   * @param {Array} rows - array of SheetRow objects
   * @param {boolean} onlyForTesting - when true, only runs for testing
   */
  startUpdaterWithSheet(venueId, sheetId, sheetType, rows, onlyForTesting = false) {
    return axios.post(serverAi + '/start_updater_with_sheet_rows', {
      venue_id: venueId,
      sheet_id: sheetId,
      sheet_type: sheetType,
      only_for_testing: String(onlyForTesting),
      rows_to_process: rows,
    })
  },

  /**
   * Get the status of a queued AI job.
   *
   * The updater runs asynchronously in a job queue on the AI backend, so the
   * frontend must poll this endpoint until the job is 'completed' (or 'failed')
   * before reloading the sheet rows. Otherwise the reload races the updater
   * and the updaterStatus column is not written yet.
   *
   * @param {string} jobId - the job id returned by startUpdaterWithSheet
   * @returns {Promise<{ job: { id, status, error, ... } }>}
   */
  getJobStatus(jobId) {
    return axios.get(serverAi + '/job_status/' + encodeURIComponent(jobId))
  },

  /**
   * Waits until the given AI job has finished (completed or failed).
   *
   * Polls the job status endpoint every `intervalMs` until the job is no
   * longer pending/processing, or until `timeoutMs` is reached.
   *
   * @param {string} jobId
   * @param {number} [intervalMs=2000]
   * @param {number} [timeoutMs=120000]
   * @returns {Promise<{ status: string, error?: string }>} the final job status
   */
  async waitForJobToFinish(jobId, intervalMs = 2000, timeoutMs = 120000) {
    const start = Date.now()
    while (Date.now() - start < timeoutMs) {
      const response = await this.getJobStatus(jobId)
      const job = response && response.data && response.data.job
      if (job && (job.status === 'completed' || job.status === 'failed')) {
        return { status: job.status, error: job.error }
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs))
    }
    throw new Error('Timeout waiting for AI job ' + jobId + ' to finish')
  },



  /**
   * List all uploaded documents for a venue
   *
   * @param {string} venueId
   * @returns {Promise<Array>} array of DroppedFileInfo objects
   *   [{ uploadDate, fileName, driveFileId }]
   */
  fetchDocuments(venueId) {
    if (!venueId) {
      throw new Error('venueId ist erforderlich zum Laden der Dokumente')
    }
    return axios.get(serverAi + '/list_dropped_files', {
      params: {
        venue_id: venueId,
      },
    })
  },

  /**
   * Download an uploaded document
   *
   * @param {string} driveFileId
   * @param {string} venueId
   * @returns {Promise<Blob>} the file content as a Blob
   */
  downloadDocument(driveFileId, venueId) {
    if (!driveFileId || !venueId) {
      throw new Error('driveFileId und venueId sind erforderlich zum Herunterladen des Dokuments')
    }
    return axios.get(serverAi + '/download_dropped_file', {
      params: {
        file_id: driveFileId,
        venue_id: venueId,
      },
      responseType: 'blob',
    })
  },


  /**
   * Upload a document
   *
   * @param {File} file
   * @param {string} venueId
   * @param {Function} [onProgress] - callback receiving upload progress (0-100)
   * @param {string} [fileName] - custom file name, defaults to file.name
   * @returns {Promise<{ drive_url: string }>}
   */
  uploadFile(file, venueId, onProgress, fileName) {
    const formData = new FormData()
    // Use custom fileName if provided, otherwise use the file's original name
    formData.append('document', file, fileName || file.name)
    formData.append('venue_id', venueId)

    return axios.post(serverAi + '/drop_file', formData, {
      onUploadProgress: (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress)
        }
      },
    })
  },
}
